import { Inject, Injectable, InjectionToken } from '@angular/core';
import { PaginatedState, PaginatedStateLoadingMode, PaginatedStateModel } from '@shared/ui';
import { ExecutionEnvironment } from '@telespot/domain';

export interface AlgorithmData {
  id: string;
  name: string;
  version: string;
  description: string;
  regulated: boolean;
  deprecated: boolean;
  environment: ExecutionEnvironment;
  numOrganizations: number;
  numWorkspaces: number;
}

export interface AlgorithmStateModel extends PaginatedStateModel<AlgorithmData> {
  environmentFilter?: ExecutionEnvironment;
  includeDeprecated: boolean;
}

export const ALGORITHM_PAGINATION_MODE = new InjectionToken<PaginatedStateLoadingMode>('ALGORITHM_PAGINATION_MODE');

@Injectable()
export class AlgorithmState extends PaginatedState<AlgorithmData, AlgorithmStateModel> {
  constructor(@Inject(ALGORITHM_PAGINATION_MODE) paginationMode: PaginatedStateLoadingMode) {
    super({ loadingMode: paginationMode }, { environmentFilter: undefined, includeDeprecated: false });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly environmentFilter$ = this.select((state) => state.environmentFilter);
  public readonly includeDeprecated$ = this.select((state) => state.includeDeprecated);

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly setEnvironmentFilter = this.updater((state, environmentFilter: ExecutionEnvironment | undefined) => ({
    ...state,
    environmentFilter,
  }));

  public readonly setIncludeDeprecated = this.updater((state, includeDeprecated: boolean) => ({
    ...state,
    includeDeprecated,
  }));

  /**
   * Helper updater for updating individual items by id (useful for deferred count loading).
   * It also finishes the loading state.
   */
  public readonly updateItemAndFinishLoading = this.updater(
    (state, { id, changes }: { id: string; changes: Partial<AlgorithmData> }) => {
      return {
        ...state,
        items: this.itemsAdapter.updateOne({ id, changes }, state.items),
        loading: false,
      };
    }
  );
}
